import React from "react";
import { NavLink, useParams } from "react-router-dom";
import {
  LayoutDashboard, BarChart3, Boxes, Bell, Upload, Settings,
  Route as RouteIcon, ClipboardCheck, FileText, Sliders, X,
} from "lucide-react";
import { cn } from "../../lib/utils";

const activeItems = [
  { to: (ws) => `/w/${ws}/dashboard`, label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: (ws) => `/w/${ws}/analyses`, label: "Analyses", icon: BarChart3, testId: "nav-analyses" },
  { to: (ws) => `/w/${ws}/bias`, label: "BIAs", icon: Boxes, testId: "nav-bias" },
  { to: (ws) => `/w/${ws}/findings`, label: "Findings", icon: Bell, testId: "nav-findings" },
  { to: (ws) => `/w/${ws}/strategies`, label: "Recovery Strategies", icon: RouteIcon, testId: "nav-strategies" },
  { to: (ws) => `/w/${ws}/remediation`, label: "Remediation", icon: ClipboardCheck, testId: "nav-remediation" },
  { to: (ws) => `/w/${ws}/reports`, label: "Reports", icon: FileText, testId: "nav-reports" },
  { to: (ws) => `/w/${ws}/import`, label: "Import", icon: Upload, testId: "nav-import" },
  { to: (ws) => `/w/${ws}/settings`, label: "Settings", icon: Settings, testId: "nav-settings" },
];

const disabledItems = [
  { label: "Rules", icon: Sliders },
];

export function Sidebar({ open, onClose }) {
  const { workspaceId } = useParams();
  return (
    <aside
      data-testid="sidebar"
      className={cn(
        "fixed inset-y-0 left-0 z-40 w-64 border-r border-slate-200 bg-white transition-transform lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 h-14">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-wider">
            RA
          </span>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-slate-900">RecoveryAssure</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">
              Deterministic BCM
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="lg:hidden text-slate-500 hover:text-slate-900"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
      </div>
      <nav className="p-3 space-y-6" aria-label="Primary navigation">
        <div>
          <div className="px-2 pb-2 text-[10px] uppercase tracking-wider text-slate-400">
            Workspace
          </div>
          <ul className="space-y-0.5">
            {activeItems.map((item) => {
              const Icon = item.icon;
              const to = workspaceId ? item.to(workspaceId) : "#";
              return (
                <li key={item.label}>
                  <NavLink
                    to={to}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm transition",
                        isActive
                          ? "bg-slate-900 text-white"
                          : "text-slate-700 hover:bg-slate-100",
                      )
                    }
                    onClick={onClose}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span>{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
        <div>
          <div className="px-2 pb-2 text-[10px] uppercase tracking-wider text-slate-400">
            Coming later
          </div>
          <ul className="space-y-0.5">
            {disabledItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.label}>
                  <span
                    aria-disabled="true"
                    className="flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm text-slate-400 cursor-not-allowed"
                    title="Coming later"
                    data-testid={`nav-disabled-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <Icon size={16} aria-hidden="true" />
                    <span>{item.label}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </nav>
    </aside>
  );
}
